import React from 'react';
import { FaTicketAlt, FaShareAlt, FaQrcode, FaFilePdf } from 'react-icons/fa';
import { DobleBadge } from '../../components/DobleBadge';
import { EntradaAfiliadoDto } from '../../types';

interface MisEntradasViewProps {
  entradas: EntradaAfiliadoDto[];
  cargando: boolean;
  cargandoConfig: boolean;
  error: string | null;
  ventaOnlineHabilitada: boolean;
  eventoNombre: string;
  eventoFinalizado?: boolean;
  onAdquirirClick: () => void;
  onNominarClick: (entrada: EntradaAfiliadoDto) => void;
  onDescargarCertificadoClick: (idEntradaDetalle: number) => void;
  onGenerarLinkClick: (entrada: EntradaAfiliadoDto) => void;
  onVerEntradaDigitalClick?: (entrada: EntradaAfiliadoDto) => void;
  onDescargarPdfClick?: (idEntradaDetalle: number) => void;
}

export const MisEntradasView: React.FC<MisEntradasViewProps> = ({
  entradas,
  cargando,
  cargandoConfig,
  error,
  ventaOnlineHabilitada,
  eventoNombre,
  eventoFinalizado = false,
  onAdquirirClick,
  onNominarClick,
  onDescargarCertificadoClick,
  onGenerarLinkClick,
  onVerEntradaDigitalClick,
  onDescargarPdfClick
}) => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-2 dark:border-strokedark pb-5">
        <div>
          <h1 className="text-title-xl font-bold text-primario-azul-900 dark:text-white">Mis Entradas</h1>
          <p className="text-sm font-semibold text-primario-celeste-800 dark:text-blue-300 mt-1">{eventoNombre}</p>
        </div>
        
        {ventaOnlineHabilitada && !eventoFinalizado && (
          <button 
            onClick={onAdquirirClick}
            className="px-5 py-2.5 bg-primario-celeste-800 hover:bg-primario-azul-900 text-white font-semibold rounded shadow transition duration-150 text-sm self-start md:self-auto"
          >
            Adquirir Nueva Entrada
          </button>
        )}
      </div>

      {!ventaOnlineHabilitada && !eventoFinalizado && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-2xl flex items-start gap-3">
          <span className="text-xl flex-shrink-0">🏛️</span>
          <div>
            <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">Adquisición de Entradas por Colegio Departamental</h4>
            <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5 leading-relaxed">
              Las entradas para este evento solo pueden adquirirse a través de su <strong>Colegio Departamental</strong> correspondiente o de forma presencial durante las acreditaciones en la sede del evento.
            </p>
          </div>
        </div>
      )}

      {(cargando || cargandoConfig) && (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs text-slate-500">Cargando tus tickets...</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-700 text-sm">
          ⚠️ {error}
        </div>
      )}

      {!cargando && !cargandoConfig && (
        <div className="grid gap-4 sm:grid-cols-1">
          {entradas.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 dark:bg-meta-4 border border-dashed border-slate-200 dark:border-strokedark rounded-3xl">
              <p className="text-slate-400 text-sm">Aún no tenés entradas adquiridas para este evento.</p>
            </div>
          ) : (
            entradas.map((entrada) => (
              <div 
                key={entrada.idEntradaDetalle}
                className="p-5 bg-white dark:bg-boxdark border border-slate-100 dark:border-strokedark rounded-2xl shadow-sm hover:shadow-md transition duration-200 flex flex-col md:flex-row md:items-center justify-between gap-4"
              >
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-primario-azul-900 dark:text-white font-bold text-sm">
                    <FaTicketAlt className="text-primario-celeste-800 flex-shrink-0" />
                    <span>{entrada.nombreCongreso || eventoNombre}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-300 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                      Ticket #{entrada.numeroEntrada}
                    </span>
                    <DobleBadge 
                      estadoEntrada={entrada.acreditada ? 'Acreditada' : (entrada.transferida ? 'Transferida' : (entrada.nominada ? 'Nominada' : 'Reservada'))} 
                      estadoPago={entrada.estadoPago} 
                    />
                  </div>

                  <div className="text-xs text-slate-600 dark:text-slate-300 space-y-1">
                    <p>
                      <strong className="text-slate-700 dark:text-slate-200">Portador: </strong> 
                      {entrada.nominada ? entrada.portadorNombre : <span className="text-amber-600 dark:text-amber-400 font-semibold">⚠️ Pendiente de nominación</span>}
                    </p>
                    {entrada.portadorDni && <p><strong className="text-slate-700 dark:text-slate-200">DNI: </strong>{entrada.portadorDni}</p>}
                    {entrada.acreditada && entrada.fechaAcreditacion && (
                      <p className="text-purple-600 dark:text-purple-400 font-semibold">✓ Acreditado el: {new Date(entrada.fechaAcreditacion).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  {/* Descarga de Certificado para cualquier entrada acreditada */}
                  {entrada.acreditada && (
                    <button
                      onClick={() => onDescargarCertificadoClick(entrada.idEntradaDetalle)}
                      className="px-4 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition duration-150 shadow-sm"
                    >
                      Descargar Certificado
                    </button>
                  )}

                  {/* Acciones para eventos vigentes no transferidos */}
                  {!eventoFinalizado && !entrada.transferida && (
                    <>
                      {/* Ver Entrada Digital Interactiva */}
                      {onVerEntradaDigitalClick && (
                        <button
                          onClick={() => onVerEntradaDigitalClick(entrada)}
                          className="px-3.5 py-2 text-xs font-bold text-white bg-primario-azul-900 hover:bg-primario-azul-800 rounded-xl transition duration-150 flex items-center space-x-1.5 shadow-sm"
                        >
                          <FaQrcode />
                          <span>Ver Entrada Digital</span>
                        </button>
                      )}

                      {/* Descargar Ticket PDF */}
                      {onDescargarPdfClick && (
                        <button
                          onClick={() => onDescargarPdfClick(entrada.idEntradaDetalle)}
                          className="px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 rounded-xl transition duration-150 flex items-center space-x-1.5 border border-slate-200 dark:border-slate-700"
                        >
                          <FaFilePdf className="text-rose-600" />
                          <span>Descargar PDF</span>
                        </button>
                      )}

                      {/* Nominación / Reasignación */}
                      {!entrada.nominada && (
                        <button
                          onClick={() => onNominarClick(entrada)}
                          className="px-3.5 py-2 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 rounded-xl transition duration-150"
                        >
                          Nominar Entrada
                        </button>
                      )}

                      {entrada.nominada && !entrada.acreditada && (
                        <button
                          onClick={() => onNominarClick(entrada)}
                          className="px-3 py-2 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/80 dark:text-slate-300 rounded-xl transition duration-150"
                        >
                          Reasignar Portador
                        </button>
                      )}

                      {/* Transferir Entrada */}
                      {!entrada.acreditada && (
                        <button
                          onClick={() => onGenerarLinkClick(entrada)}
                          className="px-3.5 py-2 text-xs font-bold text-primario-celeste-800 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-700/50 rounded-xl transition duration-150 flex items-center space-x-1.5"
                        >
                          <FaShareAlt />
                          <span>Transferir Entrada</span>
                        </button>
                      )}
                    </>
                  )}

                  {/* Leyenda para evento finalizado no acreditado */}
                  {eventoFinalizado && !entrada.acreditada && !entrada.transferida && (
                    <span className="px-3 py-1.5 text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 rounded-xl">
                      Evento Finalizado
                    </span>
                  )}

                  {/* Badge de Entrada Transferida */}
                  {entrada.transferida && (
                    <span className="px-3 py-1.5 text-xs font-extrabold text-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700/50 rounded-xl flex items-center space-x-1">
                      <span>✨ Entrada Transferida</span>
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

    </div>
  );
};
